import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger, CSSRulePlugin } from "gsap/all";

gsap.registerPlugin(ScrollTrigger, CSSRulePlugin);

export const useImageAnimation = (instanceCount = 16) => {
  const containerRefs = useRef([]);
  const imageRefs = useRef([]);

  containerRefs.current = Array(instanceCount)
    .fill()
    .map((_, i) => containerRefs.current[i] || { current: null });

  imageRefs.current = Array(instanceCount)
    .fill()
    .map((_, i) => imageRefs.current[i] || { current: null });

  const waitForImagesToLoad = () => {
    return Promise.all(
      imageRefs.current
        .filter((imgRef) => imgRef.current)
        .map(
          (imgRef) =>
            new Promise((resolve) => {
              if (imgRef.current.complete) {
                resolve();
              } else {
                imgRef.current.onload = () => resolve();
                imgRef.current.onerror = () => resolve();
              }
            })
        )
    );
  };

  useEffect(() => {
    const animateImage = (container, image) => {
      const imageReveal = CSSRulePlugin.getRule(".img-container:after");

      const fxtimeline = gsap.timeline({
        scrollTrigger: {
          trigger: container.current,
          start: "top 80%",
          end: "bottom 0%",
          onEnter: () => gsap.set(container.current, { visibility: "visible" }),
          toggleActions: "play none none none",
          markers: false,
        },
      });

      fxtimeline
        .to(imageReveal, { duration: 2, width: "0%", ease: "power2.out" })
        .from(image.current, { duration: 2, scale: 0.8, ease: "power2.out", clearProps: "all", delay: -2 });

      return fxtimeline;
    };

    waitForImagesToLoad().then(() => {
      containerRefs.current.forEach((containerRef, index) => {
        animateImage(containerRef, imageRefs.current[index]);
      });

      ScrollTrigger.refresh();
    });

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return { containerRefs, imageRefs };
};
